/**
 * Notification service for authentication and system alerts
 * Provides user-friendly notifications for authentication events
 */

export class NotificationService {
  static instance = null;
  
  constructor() {
    if (NotificationService.instance) {
      return NotificationService.instance;
    }
    
    this.notifications = [];
    this.listeners = [];
    NotificationService.instance = this;
  }

  static getInstance() {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  // Add notification listener
  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  // Notify all listeners
  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback(this.notifications);
      } catch (error) {
        console.error('Error in notification listener:', error);
      }
    });
  }

  // Add notification
  addNotification(notification) {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      timestamp: Date.now(),
      ...notification
    };
    
    this.notifications.push(newNotification);
    this.notifyListeners();
    
    // Auto-remove after duration (if specified)
    if (notification.duration) {
      setTimeout(() => {
        this.removeNotification(id);
      }, notification.duration);
    }
    
    return id;
  }

  // Remove notification
  removeNotification(id) {
    this.notifications = this.notifications.filter(n => n.id !== id);
    this.notifyListeners();
  }

  // Clear all notifications
  clearAll() {
    this.notifications = [];
    this.notifyListeners();
  }

  // Authentication-specific notifications
  showTokenExpiring(timeRemaining) {
    return this.addNotification({
      type: 'warning',
      title: 'Session Expiring',
      message: `Your session will expire in ${Math.ceil(timeRemaining / 60)} minutes. Click to refresh now.`,
      actions: [
        {
          label: 'Refresh Session',
          action: 'refresh-token',
          primary: true
        },
        {
          label: 'Dismiss',
          action: 'dismiss'
        }
      ],
      persistent: true // Don't auto-remove
    });
  }

  showTokenRefreshFailed(canRetry = true) {
    return this.addNotification({
      type: 'error',
      title: 'Session Refresh Failed',
      message: canRetry 
        ? 'Unable to refresh your session. You will be logged out soon unless you refresh manually.'
        : 'Session refresh failed. You will be redirected to login.',
      actions: canRetry ? [
        {
          label: 'Try Again',
          action: 'retry-refresh',
          primary: true
        },
        {
          label: 'Logout Now',
          action: 'logout'
        }
      ] : [
        {
          label: 'Go to Login',
          action: 'redirect-login',
          primary: true
        }
      ],
      persistent: true
    });
  }

  showNetworkError() {
    return this.addNotification({
      type: 'warning',
      title: 'Connection Issue',
      message: 'Network connection problem detected. Retrying authentication...',
      duration: 5000
    });
  }

  showRefreshSuccess() {
    return this.addNotification({
      type: 'success',
      title: 'Session Refreshed',
      message: 'Your session has been successfully renewed.',
      duration: 3000
    });
  }

  showLogoutWarning(secondsRemaining) {
    return this.addNotification({
      type: 'error',
      title: 'Automatic Logout',
      message: `You will be logged out in ${secondsRemaining} seconds due to session expiry.`,
      actions: [
        {
          label: 'Stay Logged In',
          action: 'cancel-logout',
          primary: true
        }
      ],
      persistent: true
    });
  }
}

// Export singleton instance
export default NotificationService.getInstance();